import {elapsed, parseRun} from "./stopwatch.state";

/**
 * The timer as pure data (feature plan 0073): no DOM, no storage, the clock passed in — like the stopwatch
 * (`stopwatch.state.ts`, wall-clock time for the same reason: the state lives in `localStorage`).
 *
 * A run counts `durationMs` down: `elapsedMs` up to the last pause, `runningSince` the start of the current
 * stretch, `null` while paused. `basedOnMs` is the preset (dialog) the state was built on — a changed preset
 * replaces an *unstarted* state, never a started one.
 *
 * Terms: *unstarted* = nothing elapsed and not running; *done* = duration > 0 and nothing left. A done timer
 * counts as started. `±` change the run's duration by a minute: never below one second left, never above 180
 * minutes, never once done (the blinking ends only with reset or start).
 */
export interface TimerState {
    readonly durationMs: number;
    readonly elapsedMs: number;
    readonly runningSince: number | null;
    readonly basedOnMs: number;
}

export const TIMER_MIN_MINUTES = 1;
export const TIMER_MAX_MINUTES = 180;
export const TIMER_DEFAULT_MINUTES = 5;
export const TIMER_STEP_MS = 60000;
/** "−" leaves at least this much — it never ends a run. */
export const TIMER_MIN_REST_MS = 1000;
const MAX_MS = TIMER_MAX_MINUTES * TIMER_STEP_MS;

/** A preset in whole minutes from 1 to 180 (the dialog's rule). */
export function isTimerMinutes(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value)
        && value >= TIMER_MIN_MINUTES && value <= TIMER_MAX_MINUTES;
}

/** The preset minutes stored with a tile; missing or invalid → the default. */
export function timerMinutes(options: Readonly<Record<string, unknown>>): number {
    return isTimerMinutes(options.minutes) ? options.minutes : TIMER_DEFAULT_MINUTES;
}

export function presetMs(options: Readonly<Record<string, unknown>>): number {
    return timerMinutes(options) * TIMER_STEP_MS;
}

export function reset(preset: number): TimerState {
    return {durationMs: preset, elapsedMs: 0, runningSince: null, basedOnMs: preset};
}

export function isRunning(state: TimerState): boolean {
    return state.runningSince !== null;
}

export function isUnstarted(state: TimerState): boolean {
    return state.elapsedMs === 0 && state.runningSince === null;
}

export function remaining(state: TimerState, now: number): number {
    return Math.max(0, state.durationMs - elapsed(state, now));
}

export function isDone(state: TimerState, now: number): boolean {
    return state.durationMs > 0 && remaining(state, now) === 0;
}

/** When a running timer reaches zero (wall clock); `null` while paused. */
export function endsAt(state: TimerState): number | null {
    return state.runningSince === null ? null : state.runningSince + state.durationMs - state.elapsedMs;
}

/** Start or resume; after the end a new run with the preset (one click instead of reset and start). */
export function start(state: TimerState, now: number, preset: number): TimerState {
    if (isDone(state, now)) {
        return {...reset(preset), runningSince: now};
    }
    return state.runningSince === null ? {...state, runningSince: now} : state;
}

/** Pause; the elapsed time never exceeds the duration. */
export function pause(state: TimerState, now: number): TimerState {
    if (state.runningSince === null) {
        return state;
    }
    return {...state, elapsedMs: Math.min(state.durationMs, elapsed(state, now)), runningSince: null};
}

export function canDecrease(state: TimerState, now: number): boolean {
    return !isDone(state, now) && remaining(state, now) - TIMER_STEP_MS >= TIMER_MIN_REST_MS;
}

export function canIncrease(state: TimerState, now: number): boolean {
    return !isDone(state, now) && state.durationMs + TIMER_STEP_MS <= MAX_MS;
}

/** One minute less (`-1`) or more (`+1`) for this run — unchanged where the rules above forbid it. */
export function adjust(state: TimerState, direction: -1 | 1, now: number): TimerState {
    if (direction < 0 ? !canDecrease(state, now) : !canIncrease(state, now)) {
        return state;
    }
    return {...state, durationMs: state.durationMs + direction * TIMER_STEP_MS};
}

/** The end: stopped at zero. The same value from the same state, whoever computes it. */
export function finish(state: TimerState): TimerState {
    return {...state, elapsedMs: state.durationMs, runningSince: null};
}

/** A changed preset replaces an unstarted state (and a `±` made before the start); a started one keeps its run. */
export function follow(state: TimerState, preset: number): TimerState {
    return isUnstarted(state) && state.basedOnMs !== preset ? reset(preset) : state;
}

function isDurationMs(value: unknown): value is number {
    return typeof value === 'number' && isTimerMinutes(value / TIMER_STEP_MS);
}

/**
 * A stored value as a state, or `null` when it is not one — without a preset, so the watcher (which knows no
 * tiles) and the element read it alike. The run part as the stopwatch's `parseRun`; the duration whole minutes
 * from 1 to 180; the elapsed time at most the duration; an invalid `basedOnMs` is the duration. The element
 * starts afresh with its preset where this is `null`.
 */
export function parseStored(raw: unknown, now: number): TimerState | null {
    const run = parseRun(raw, now);
    if (run === null) {
        return null;
    }
    const {durationMs, basedOnMs} = raw as Record<string, unknown>;
    if (!isDurationMs(durationMs)) {
        return null;
    }
    return {
        durationMs,
        elapsedMs: Math.min(run.elapsedMs, durationMs),
        runningSince: run.runningSince,
        basedOnMs: isDurationMs(basedOnMs) ? basedOnMs : durationMs,
    };
}
